import type { CSSProperties, ReactNode } from "react";

import { useT } from "@/3-features/i18n/useT";

/**
 * The DEC waterfalls: two bridges reading left to right, one climbing and one
 * descending. Each is drawn full-size in its §-scene and as a thumbnail behind its
 * bento tile, same proportions, different skin, so only the geometry and the layout
 * primitives live here.
 *
 * `WF`, §5 EBITDA: today's EBITDA, the GO cost that disappears, the revenue that
 * arrives, and the total they add up to. Heights are percentages of the plot area:
 * 52 + 12 + 24 = 88.
 */
export const WF = { now: 52, go: 12, rev: 24 } as const;
export const WF_TOTAL = WF.now + WF.go + WF.rev;

/**
 * `WFN`, §3A riduzione prevedibile, the same bridge running downwards: the total energy
 * bill (costo energia + costo GO), minus the GO's worth, minus the earnings on the sold
 * energy, equals the net cost. GO and profit segments reuse `WF`'s proportions (12/24)
 * and the bill sums to the same 88, so the two scenes mirror exactly: this one lands on
 * the 52 the EBITDA bridge starts from.
 */
export const WFN = { energy: 76, go: WF.go, prof: WF.rev } as const;
/** the "oggi" column: the whole energy bill */
const WFN_TOTAL = WFN.energy + WFN.go;
/** the result column: what is left once both subtractions are done */
export const WFN_NET = WFN_TOTAL - WFN.go - WFN.prof;

/** One column of the waterfall, bars stack up from the baseline. */
export function WfColumn({ children }: { children: ReactNode }) {
  return <div className="flex h-full flex-col justify-end">{children}</div>;
}

/** Vertical padding that lifts a bar to where the previous column ended. */
export function WfLift({ h }: { h: number }) {
  return <div style={{ height: `${h}%` }} />;
}

/**
 * One bar. The skin scales from the bottom (`scaleY`), so an overlaid label has to be
 * a sibling rather than a child or the text would stretch with the growth. The caller
 * animates `.${cls}` for the bar and `.${cls}-t` for the label.
 *
 * `mode` picks how the bar arrives: `"grow"` scales up from the baseline (a quantity
 * being built), `"fade"` only appears (a marker for a quantity being taken away, which
 * never grew in the first place). The starting frame has to be inline either way, so the
 * bar is hidden before the timeline is built.
 */
export function WfBar({
  cls,
  h,
  className,
  label,
  labelClassName,
  mode = "grow",
  settled = false,
  minHeightPx,
}: {
  cls: string;
  h: number;
  className: string;
  label?: ReactNode;
  labelClassName?: string;
  mode?: "grow" | "fade";
  /** Render the final (visible) frame, for a static waterfall with no timeline. */
  settled?: boolean;
  /** Floor on the bar's rendered height, so a tiny value still fits its label. */
  minHeightPx?: number;
}) {
  const hidden: CSSProperties = settled ? {} : mode === "grow" ? { transform: "scaleY(0)", transformOrigin: "bottom" } : { opacity: 0 };
  const labelHidden: CSSProperties = settled ? {} : { opacity: 0 };
  const min = minHeightPx ? { minHeight: `${minHeightPx}px` } : {};
  const bar = <div className={`${cls} ${className}`} style={{ height: label ? "100%" : `${h}%`, ...min, ...hidden }} />;
  if (!label) return bar;
  return (
    <div className="relative" style={{ height: `${h}%`, ...min }}>
      <div className={`${cls} absolute inset-0 ${className}`} style={hidden} />
      <span className={`${cls}-t absolute inset-0 ${labelClassName}`} style={labelHidden}>
        {label}
      </span>
    </div>
  );
}

/** The tween both scenes grow their columns with, spread it and add duration/ease. */
export const WF_GROW = { scaleY: [0, 1] as [number, number] };

const SEG_TONES = {
  grey: "bg-zinc-200",
  prof: "bg-dec-green/20 ring-1 ring-inset ring-dec-green/35",
  go: "bg-dec-green",
  ghost: "border-2 border-dashed border-dec-green/60 bg-dec-green/5",
} as const;

const SEG_INK = {
  grey: "text-zinc-600",
  prof: "text-dec-green-dark",
  go: "text-white",
  ghost: "text-dec-green-dark",
} as const;

/** A waterfall bar in the §-scenes' skin: rounded, tinted, with its label overlaid. */
export function WfSeg({ cls, h, tone, label, mode, settled, minHeightPx }: { cls: string; h: number; tone: keyof typeof SEG_TONES; label: ReactNode; mode?: "grow" | "fade"; settled?: boolean; minHeightPx?: number }) {
  return (
    <WfBar
      cls={cls}
      h={h}
      mode={mode}
      settled={settled}
      minHeightPx={minHeightPx}
      className={`rounded-md md:rounded-lg ${SEG_TONES[tone]}`}
      label={label}
      labelClassName={`flex flex-col items-center justify-center px-1.5 text-center text-[0.625rem] md:text-[0.6875rem] font-semibold leading-tight [text-wrap:balance] ${SEG_INK[tone]}`}
    />
  );
}

/**
 * The Oggi / DEC Energy / Risultato reading bands under a four-column bridge. With
 * `clsPrefix` they start hidden and the caller's timeline fades in `.{p}-band-now|dec|res`
 * on its beats; without it they render settled (the explorer's static waterfalls).
 */
export function WfBands({ clsPrefix, className }: { clsPrefix?: string; className?: string }) {
  const { t } = useT("common");
  const bands = [
    { key: "now", label: t("waterfall.today"), tone: "col-start-1 bg-zinc-100 text-zinc-500" },
    { key: "dec", label: "DEC Energy", tone: "col-start-2 col-end-4 bg-dec-green/10 text-dec-green-dark" },
    { key: "res", label: t("waterfall.result"), tone: "col-start-4 bg-dec-green text-white" },
  ];
  return (
    <div className={`grid grid-cols-4 gap-2 md:gap-3 ${className ?? ""}`}>
      {bands.map((b) => (
        <div
          key={b.key}
          className={`${clsPrefix ? `${clsPrefix}-band-${b.key}` : ""} ${b.tone} rounded-md md:rounded-lg py-1.5 text-center text-[0.5625rem] md:text-[0.6875rem] font-bold uppercase tracking-[0.14em]`}
          style={clsPrefix ? { opacity: 0 } : undefined}
        >
          {b.label}
        </div>
      ))}
    </div>
  );
}

/**
 * The whole §-scene card both bridges share: title, the equation header, the four-column
 * plot (the caller's `children`) and the `WfBands`. The caller keeps only its own columns
 * and timeline; `clsPrefix` namespaces the band classes the timeline fades in.
 */
export function WaterfallCard({ title, heads, clsPrefix, children }: { title: string; heads: WfHead[]; clsPrefix: string; children: ReactNode }) {
  return (
    <div className="w-full max-w-xl card p-5 md:p-8 shadow-sm">
      <p className="label font-semibold mb-4 md:mb-6">{title}</p>

      <WfHeads className="mb-3 md:mb-4" items={heads} />

      <div className="grid h-[15rem] grid-cols-4 gap-2 md:h-[20rem] md:gap-3">{children}</div>

      <WfBands clsPrefix={clsPrefix} className="mt-2 md:mt-3" />
    </div>
  );
}

/**
 * The Oggi / DEC Energy / Risultato row under a mini waterfall, the bento tiles'
 * compact stand-in for the full bands `WaterfallCard` draws. Grid mirrors the bars'.
 */
export function WfLegend() {
  const { t } = useT("common");
  return (
    <div className="mt-2 grid grid-cols-4 gap-2 text-[0.5625rem] font-bold uppercase tracking-[0.14em] md:gap-3">
      <span className="col-start-1 rounded bg-zinc-100 py-1 text-center text-zinc-500">{t("waterfall.today")}</span>
      <span className="col-start-2 col-end-4 rounded bg-dec-green/10 py-1 text-center text-dec-green-dark">DEC Energy</span>
      <span className="col-start-4 rounded bg-dec-green py-1 text-center text-white">{t("waterfall.result")}</span>
    </div>
  );
}

/** One cell of the equation header: the column's name, and the operator that precedes it. */
type WfHead = { cls: string; label: ReactNode; op?: ReactNode; ink?: string };

/**
 * The equation above the columns, `a + b + c = d`, or `a − b − c = d`, so the bridge
 * reads as arithmetic and not just as bars. Grid and gaps mirror the bars' own, which is
 * what puts each operator in the gutter it belongs to. The caller fades in `.${cls}` per
 * cell, on the beat that grows the column underneath it.
 */
function WfHeads({ items, className }: { items: WfHead[]; className?: string }) {
  return (
    <div className={`grid grid-cols-4 gap-2 md:gap-3 ${className ?? ""}`}>
      {items.map((it) => (
        <div key={it.cls} className={`${it.cls} relative flex items-center justify-center text-center`} style={{ opacity: 0 }}>
          {it.op && (
            <span className="absolute -left-2.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-zinc-400 md:-left-3 md:text-base" aria-hidden>
              {it.op}
            </span>
          )}
          <span className={`block text-[0.5625rem] md:text-[0.6875rem] font-semibold leading-tight [text-wrap:balance] ${it.ink ?? "text-zinc-500"}`}>
            {it.label}
          </span>
        </div>
      ))}
    </div>
  );
}
